import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Trash2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Search,
} from "lucide-react";
import { CIVIL_UPLOAD_TYPE, isCivilDocType, civilDocShort } from "./civilDocs";
import PageHeader from "./PageHeader";
import { docTypeMeta, getDocUrl as docUrl } from "./docTypes";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

// все виды документов РФ (СОР, СОРБ, …) — одна вкладка
const docGroup = (t) => (isCivilDocType(t) ? CIVIL_UPLOAD_TYPE : t || "passport");

const getDocUrl = (item) => docUrl(item.document_type || "passport", item.id);

const TABS = [
  { key: "all", label: "Все", tone: null },
  { key: "passport", label: "Паспорта", tone: "lavender" },
  { key: "drivers_license", label: "Права", tone: "mint" },
  { key: "contract", label: "Договоры", tone: "peach" },
  { key: CIVIL_UPLOAD_TYPE, label: "Документы РФ", tone: "butter" },
];

const DocTypeCell = ({ type }) => {
  const meta = docTypeMeta(type);
  const { Icon } = meta;
  const label = isCivilDocType(type) ? civilDocShort(type) : meta.label;
  return (
    <span className="hist-type">
      <span className={`hist-type-icon tone-${meta.tone}`}>
        <Icon size={20} aria-hidden="true" />
      </span>
      {label}
    </span>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <nav className="hist-pages" aria-label="Страницы">
    <button
      type="button"
      className="px-icon-btn is-small"
      aria-label="Предыдущая страница"
      disabled={currentPage <= 1}
      onClick={() => onPageChange(currentPage - 1)}
    >
      <ChevronLeft size={18} aria-hidden="true" />
    </button>
    <span className="hist-pages-info" aria-live="polite">
      {currentPage} из {totalPages || 1}
    </span>
    <button
      type="button"
      className="px-icon-btn is-small"
      aria-label="Следующая страница"
      disabled={currentPage >= (totalPages || 1)}
      onClick={() => onPageChange(currentPage + 1)}
    >
      <ChevronRight size={18} aria-hidden="true" />
    </button>
  </nav>
);

function HistoryPage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [searchQuery, setSearchQuery] = useState("");

  const [docTypeFilter, setDocTypeFilter] = useState("all");

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const fetchRecords = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/passports`, {
        params: { page, per_page: 10 },
      });
      const data = res.data;
      if (data?.items) {
        setRecords(data.items);
        setPagination({
          page: data.page || 1,
          pages: data.pages || 1,
          total: data.total || 0,
        });
      } else if (data?.passports) {
        setRecords(data.passports);
        setPagination({
          page: data.current_page || 1,
          pages: data.total_pages || 1,
          total: data.total_items || 0,
        });
      } else {
        setRecords(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error("Failed to load records", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      let userTpls = [],
        publicTpls = [];
      try {
        const r = await axios.get(`${API_BASE_URL}/api/user-templates`);
        userTpls = r.data || [];
      } catch {}
      try {
        const r = await axios.get(`${API_BASE_URL}/api/user-templates/public`);
        publicTpls = r.data || [];
      } catch {}
      const all = [...userTpls, ...publicTpls];
      setTemplates(all);
      if (all.length > 0) setSelectedTemplate(String(all[0].id));
    } catch (e) {
      console.error("Failed to load templates", e);
    }
  }, []);

  useEffect(() => {
    fetchRecords(1);
    fetchTemplates();
  }, [fetchRecords, fetchTemplates]);

  const filtered = records.filter((item) => {
    if (
      docTypeFilter !== "all" &&
      docGroup(item.document_type) !== docTypeFilter
    )
      return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.full_name && item.full_name.toLowerCase().includes(q)) ||
      (item.passport_number &&
        item.passport_number.toLowerCase().includes(q)) ||
      (item.filename && item.filename.toLowerCase().includes(q))
    );
  });

  const typeCounts = records.reduce((acc, r) => {
    const t = docGroup(r.document_type);
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const toggleId = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.id)));
    }
  };

  const selectedDownloadable = Array.from(selectedIds).filter((id) => {
    const rec = records.find((r) => r.id === id);
    return rec && rec.document_type !== "contract";
  });

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Удалить этот документ из истории?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/passports/${id}`);
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      fetchRecords(pagination.page);
    } catch (e) {
      console.error("Delete failed", e);
      alert("Ошибка удаления");
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Удалить ${selectedIds.size} выбранных документов?`))
      return;
    for (const id of selectedIds) {
      try {
        await axios.delete(`${API_BASE_URL}/api/passports/${id}`);
      } catch {}
    }
    setSelectedIds(new Set());
    fetchRecords(pagination.page);
  };

  const handleBatchDownload = async (format) => {
    if (selectedDownloadable.length === 0) {
      alert(
        "Нет подходящих документов для скачивания (договоры не поддерживают шаблоны)",
      );
      return;
    }
    const setter = format === "docx" ? setDownloadingWord : setDownloadingPdf;
    setter(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/passports/batch-download`,
        { ids: selectedDownloadable, format, template_id: selectedTemplate },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download =
        format === "docx" ? "documents_word.zip" : "documents_pdf.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Batch download failed", e);
      alert("Ошибка скачивания архива");
    } finally {
      setter(false);
    }
  };

  const allSelected =
    filtered.length > 0 && selectedIds.size === filtered.length;

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString("ru-RU")}, ${d.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  return (
    <>
      <PageHeader
        title="История"
        subtitle={`Всего ${pagination.total} ${
          pagination.total % 10 === 1 && pagination.total % 100 !== 11
            ? "документ"
            : [2, 3, 4].includes(pagination.total % 10) &&
                ![12, 13, 14].includes(pagination.total % 100)
              ? "документа"
              : "документов"
        }`}
      >
        <button
          type="button"
          className="px-icon-btn"
          aria-label="Обновить список"
          onClick={() => fetchRecords(pagination.page)}
          disabled={loading}
        >
          <RefreshCw size={20} className={loading ? "spin" : ""} aria-hidden="true" />
        </button>
      </PageHeader>

      <div className="hist-filters">
        <div className="hist-tabs" role="radiogroup" aria-label="Тип документа">
          {TABS.map((tab) => {
            const count =
              tab.key === "all" ? records.length : typeCounts[tab.key] || 0;
            const active = docTypeFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="radio"
                aria-checked={active}
                className={`hist-tab${tab.tone ? ` tone-${tab.tone}` : ""}${active ? " is-active" : ""}`}
                onClick={() => {
                  setDocTypeFilter(tab.key);
                  setSelectedIds(new Set());
                }}
              >
                {tab.label}
                <span className="hist-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
        <label className="hist-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            aria-label="Поиск"
            placeholder="ФИО, номер документа или файл"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>

      {selectedIds.size > 0 && (
        <div className="hist-batch" role="region" aria-label="Действия с выбранными">
          <span className="hist-batch-count">Выбрано: {selectedIds.size}</span>
          {selectedDownloadable.length > 0 && templates.length > 0 && (
            <>
              <label className="hist-batch-template">
                <span className="sr-only">Шаблон для архива</span>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.label || t.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="hist-batch-btn"
                onClick={() => handleBatchDownload("docx")}
                disabled={downloadingWord || downloadingPdf}
              >
                <Download size={18} aria-hidden="true" />
                {downloadingWord ? "Готовим…" : "Word · архив"}
              </button>
              <button
                type="button"
                className="hist-batch-btn"
                onClick={() => handleBatchDownload("pdf")}
                disabled={downloadingWord || downloadingPdf}
              >
                <Download size={18} aria-hidden="true" />
                {downloadingPdf ? "Готовим…" : "PDF · архив"}
              </button>
            </>
          )}
          {selectedDownloadable.length < selectedIds.size && (
            <span className="hist-batch-note">
              Договоры в архив не входят:{" "}
              {selectedIds.size - selectedDownloadable.length}
            </span>
          )}
          <button
            type="button"
            className="hist-batch-btn is-danger"
            onClick={handleBatchDelete}
          >
            <Trash2 size={18} aria-hidden="true" /> Удалить
          </button>
        </div>
      )}

      <div className="px-table-wrap" style={{ marginTop: 16 }}>
        <table className="hist-table">
          <thead>
            <tr>
              <th scope="col" className="hist-check">
                <input
                  type="checkbox"
                  aria-label="Выбрать все на странице"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th scope="col">Документ</th>
              <th scope="col">ФИО / файл</th>
              <th scope="col">Номер</th>
              <th scope="col">Загружен</th>
              <th scope="col">
                <span className="sr-only">Действия</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="hist-empty">
                  Загружаем…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="6" className="hist-empty">
                  {searchQuery || docTypeFilter !== "all"
                    ? "Ничего не найдено"
                    : "Пока нет документов"}
                </td>
              </tr>
            ) : (
              filtered.map((item) => {
                const docType = item.document_type || "passport";
                const isSelected = selectedIds.has(item.id);
                const name = item.full_name || item.filename || `№ ${item.id}`;
                return (
                  <tr
                    key={item.id}
                    className={isSelected ? "is-selected" : ""}
                    onClick={() => navigate(getDocUrl(item))}
                  >
                    <td className="hist-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Выбрать: ${name}`}
                        checked={isSelected}
                        onChange={() => toggleId(item.id)}
                      />
                    </td>
                    <td>
                      <DocTypeCell type={docType} />
                    </td>
                    <td>
                      <span className="px-doc-name">
                        {item.full_name || (
                          <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
                            Без имени
                          </span>
                        )}
                      </span>
                      <span className="px-doc-sub">{item.filename}</span>
                    </td>
                    <td style={{ fontWeight: 600, color: item.passport_number ? "inherit" : "var(--text-muted)" }}>
                      {item.passport_number || "—"}
                    </td>
                    <td style={{ color: "var(--text-soft)", whiteSpace: "nowrap" }}>
                      {fmtDate(item.created_at)}
                    </td>
                    <td className="hist-actions" onClick={(e) => e.stopPropagation()}>
                      <Link to={getDocUrl(item)} className="hist-open">
                        Открыть
                      </Link>
                      <button
                        type="button"
                        className="px-icon-btn is-small hist-delete"
                        aria-label={`Удалить: ${name}`}
                        onClick={(e) => handleDelete(item.id, e)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="hist-footer">
        <span className="px-page-sub" style={{ margin: 0 }}>
          {filtered.length !== records.length &&
            `Показано ${filtered.length} из ${records.length} на странице`}
        </span>
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          onPageChange={(p) => fetchRecords(p)}
        />
      </div>
    </>
  );
}

export default HistoryPage;
